import React, { useMemo } from "react";

import cx from "classnames";

import { Flex, FlexChild } from "../Flex";
import { Spacer } from "../Grid/Spacer";
import { Icon } from "../Icon";
import { THEME_COLORS } from "../../theme/themeTokens";
import { Tooltip } from "../Tooltip/Tooltip";

import styles from "./Fieldset.scss";

interface FieldsetProps {
  /** The label for the fieldset */
  label?: React.ReactNode;
  /** Error message displayed within the fieldset */
  error?: string;
  /** ID of the form the fieldset belongs to */
  formId?: string;
  /** Additional CSS classes for the fieldset */
  className?: string;
  /** Whether the fieldset is disabled */
  disabled?: boolean;
  /** Text indicating that a field is optional */
  optionalLabelText?: string;
  /** Text displayed in the upper right of the fieldset */
  upperRightLabelText?: string;
  /** Tooltip content displayed within the fieldset */
  tooltip?: string | JSX.Element;
  /** Test ID for the fieldset */
  "data-testid"?: string;
  /** Helper text displayed within the fieldset */
  helperText?: React.ReactNode;
  /** Variant of the fieldset for styling */
  variant?: "highlighted" | "transparent";
  /** Position of the helper text in relation to the children */
  helperTextPosition?: "afterChildren" | "beforeChildren";
  /** Type of the legend within the fieldset */
  legendType?: "basic" | "radiogroup";
  /** Node to replace the default legend element */
  legendNode?: JSX.Element;
}

/**
 * Get the class for the legend based on the type passed in
 * @param legendType Type of the legend
 * @returns CSS class for the legend
 */
function getLegendClass(legendType: "basic" | "radiogroup"): string | null {
  switch (legendType) {
    case "radiogroup":
      return styles["radio-fieldset-legend"];
    default:
      return null;
  }
}

/**
 * A wrapper for Fieldset, and its corresponding legend.
 * Useful for grouping elements in a form.
 * Screen readers will read the text provided to the legend via the label prop.
 */
export function Fieldset({
  children,
  error,
  label,
  formId,
  optionalLabelText,
  upperRightLabelText,
  tooltip,
  className,
  disabled,
  helperText,
  helperTextPosition = "beforeChildren",
  "data-testid": dataTestId,
  legendType = "basic",
  legendNode,
  variant = "transparent",
}: React.PropsWithChildren<FieldsetProps>) {
  const fieldsetClasses = cx(className, [styles["fieldset"]]);

  const helperTextClassName = useMemo(
    () =>
      cx(styles["helper-text"], {
        [styles["helper-text-before-children"]]:
          helperTextPosition === "beforeChildren",
        [styles["helper-text-after-children"]]:
          helperTextPosition !== "beforeChildren",
      }),
    [helperTextPosition]
  );

  const legendClasses = cx(styles["legend"], getLegendClass(legendType));

  const helperTextNode = helperText ? (
    <p className={helperTextClassName}>{helperText}</p>
  ) : null;

  const childrenNode =
    helperTextPosition === "beforeChildren" ? (
      <>
        {helperTextNode}
        {children}
      </>
    ) : (
      <>
        {children}
        {helperTextNode}
      </>
    );

  const errorNode = error ? (
    <Flex alignItems="center">
      <FlexChild>
        <Spacer size="tiny" />
        <Icon icon="warning" size={12} color={THEME_COLORS.statusError} />
      </FlexChild>
      <Spacer size="tiny" orientation="horizontal" />
      <span className={styles["error"]}>{error}</span>
    </Flex>
  ) : null;

  const fieldSetContents =
    variant === "transparent" ? (
      <>
        {childrenNode}
        {errorNode}
      </>
    ) : (
      <div className={styles["highlighted"]}>
        {childrenNode}
        {errorNode}
      </div>
    );

  return (
    <fieldset
      disabled={disabled}
      data-testid={dataTestId}
      className={fieldsetClasses}
      form={formId || undefined}
    >
      {legendNode || (
        <legend
          data-testid={dataTestId ? `${dataTestId}-legend` : undefined}
          className={legendClasses}
        >
          <Flex alignItems="center">
            {label}
            {tooltip && (
              <>
                <Spacer orientation="horizontal" size="small" />
                <Tooltip
                  onClick={
                    // istanbul ignore next - trivial behavior
                    (e) => e.preventDefault()
                  } // prevent focus when clicked
                  icon={true}
                  content={tooltip}
                />
              </>
            )}
            {optionalLabelText && (
              <>
                <span className={styles["fieldset-optional-label"]}>
                  {optionalLabelText}
                </span>
              </>
            )}
            {upperRightLabelText && (
              <>
                <FlexChild grow={1} />
                <span className={styles["fieldset-upper-right-label"]}>
                  {upperRightLabelText}
                </span>
              </>
            )}
          </Flex>
        </legend>
      )}
      {fieldSetContents}
    </fieldset>
  );
}

export type { FieldsetProps };
